/**
 * The dockview workspace shell — the Phase 1 UI, now a page under `/`.
 *
 * Extracted so `/projects` can render a different page without
 * duplicating the ribbon/tree/pm/viewport orchestration.
 */
import { useMemo } from 'react'
import { HotkeysProvider, useHotkeys } from '@blueprintjs/core'
import {
  DockviewReact,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
} from 'dockview'
import 'dockview/dist/styles/dockview.css'

import { Ribbon } from '../panels/Ribbon'
import { FeatureTreePanel } from '../panels/FeatureTree'
import { PropertyManagerPanel } from '../panels/PropertyManager'
import { ViewportPanel } from '../panels/Viewport'
import { BottomPanel } from '../panels/BottomPanel'
import { StatusBar } from '../panels/StatusBar'
import { PreferencesDialog } from '../dialogs/PreferencesDialog'
import { AboutDialog } from '../dialogs/AboutDialog'
import { ToolImportDialog } from '../dialogs/ToolImportDialog'
import { useStore } from '../store'

const PANEL_COMPONENTS = {
  featureTree: ({ containerApi: _ca }: IDockviewPanelProps) => {
    const store = useStore()
    return <FeatureTreePanel store={store} />
  },
  propertyManager: () => {
    const store = useStore()
    return <PropertyManagerPanel store={store} />
  },
  viewport: () => {
    const store = useStore()
    return <ViewportPanel store={store} />
  },
  bottom: () => {
    const store = useStore()
    return <BottomPanel store={store} />
  },
}

export function SingleShell() {
  const store = useStore()
  const theme = store.preferences.theme

  const hotkeys = useMemo(
    () => [
      { combo: 'escape', global: true, label: 'Cancel pick', onKeyDown: () => store.cancelPendingOperation() },
      { combo: 'enter', global: true, label: 'Commit', onKeyDown: () => store.commitPendingOperation() },
      { combo: 'f', global: true, label: 'Fit', onKeyDown: () => {} },
      {
        combo: 'del', global: true, label: 'Delete op',
        onKeyDown: () => {
          const sel = store.selection
          if (sel?.kind === 'operation') store.deleteOperation(sel.id)
        },
      },
    ],
    [store],
  )
  useHotkeys(hotkeys)

  const onReady = (event: DockviewReadyEvent) => {
    const api = event.api
    api.addPanel({ id: 'featureTree', component: 'featureTree', title: 'Feature tree' })
    api.addPanel({
      id: 'viewport',
      component: 'viewport',
      title: 'Viewport',
      position: { referencePanel: 'featureTree', direction: 'right' },
    })
    api.addPanel({
      id: 'propertyManager',
      component: 'propertyManager',
      title: 'Property manager',
      position: { referencePanel: 'viewport', direction: 'right' },
    })
    api.addPanel({
      id: 'bottom',
      component: 'bottom',
      title: 'Timeline / Messages',
      position: { referencePanel: 'viewport', direction: 'below' },
    })
    const ft = api.getPanel('featureTree')?.group
    if (ft) ft.api.setSize({ width: 260 })
    const pm = api.getPanel('propertyManager')?.group
    if (pm) pm.api.setSize({ width: 340 })
    const b = api.getPanel('bottom')?.group
    if (b) b.api.setSize({ height: 160 })
  }

  return (
    <HotkeysProvider>
      <div className={`app ${theme === 'dark' ? 'bp5-dark' : ''}`}>
        <Ribbon store={store} />
        <div className="dockview-host">
          <DockviewReact
            components={PANEL_COMPONENTS}
            onReady={onReady}
            className={theme === 'dark' ? 'dockview-theme-abyss' : 'dockview-theme-light'}
          />
        </div>
        <StatusBar store={store} />
        <PreferencesDialog
          isOpen={store.openDialog === 'prefs'}
          onClose={store.closeDialog}
          store={store}
        />
        <AboutDialog isOpen={store.openDialog === 'about'} onClose={store.closeDialog} />
        <ToolImportDialog
          isOpen={store.openDialog === 'toolImport'}
          onClose={store.closeDialog}
          store={store}
        />
      </div>
    </HotkeysProvider>
  )
}
